import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { mediaQueries } from 'shared/styles';

import { BaseSlideWrapper, BaseSlideContent, BaseSlideCta } from './login.styles';

/* ── Shaders (raw WebGL — no Three.js; ACES+sRGB baked in) ─────────── */
const VERT = `attribute vec2 aPos; varying vec2 vUv; void main(){ vUv=aPos*0.5+0.5; gl_Position=vec4(aPos,0.0,1.0); }`;
const FRAG = `
  precision highp float; varying vec2 vUv; uniform float uTime; uniform float uAspect; uniform int uOctaves;
  vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;} vec2 mod289(vec2 x){return x-floor(x*(1./289.))*289.;}
  vec3 permute(vec3 x){return mod289(((x*34.)+1.)*x);}
  float snoise(vec2 v){
    const vec4 C=vec4(.211324865,.366025404,-.577350269,.024390244);
    vec2 i=floor(v+dot(v,C.yy)),x0=v-i+dot(i,C.xx);
    vec2 i1=(x0.x>x0.y)?vec2(1,0):vec2(0,1);
    vec4 x12=x0.xyxy+C.xxzz; x12.xy-=i1; i=mod289(i);
    vec3 p=permute(permute(i.y+vec3(0,i1.y,1))+i.x+vec3(0,i1.x,1));
    vec3 m=max(.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.); m=m*m; m=m*m;
    vec3 x=2.*fract(p*C.www)-1.,h=abs(x)-.5,ox=floor(x+.5),a0=x-ox;
    m*=1.79284291-.85373472*(a0*a0+h*h);
    vec3 g; g.x=a0.x*x0.x+h.x*x0.y; g.yz=a0.yz*x12.xz+h.yz*x12.yw; return 130.*dot(m,g);
  }
  float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<3;i++){if(i>=uOctaves)break;v+=a*snoise(p);p*=2.02;a*=.5;}return v;}
  vec3 RRTAndODTFit(vec3 v){vec3 a=v*(v+0.0245786)-0.000090537;vec3 b=v*(0.983729*v+0.4329510)+0.238081;return a/b;}
  vec3 acesFilmic(vec3 c){
    const mat3 i=mat3(0.59719,0.07600,0.02840,0.35458,0.90834,0.13383,0.04823,0.01566,0.83777);
    const mat3 o=mat3(1.60475,-0.10208,-0.00327,-0.53108,1.10813,-0.07276,-0.07367,-0.00605,1.07602);
    c*=1.0/0.6; c=i*c; c=RRTAndODTFit(c); c=o*c; return clamp(c,0.0,1.0);
  }
  vec3 sRGBenc(vec3 c){return mix(pow(c,vec3(0.41666))*1.055-0.055,c*12.92,vec3(lessThanEqual(c,vec3(0.0031308))));}
  void main(){
    vec2 uv=vUv,p=uv*2.-1.; p.x*=max(uAspect,1.); p.y*=max(1./uAspect,1.);
    vec2 q=vec2(fbm(p*.6+vec2(uTime*.018,-uTime*.012)),fbm(p*.6+vec2(-uTime*.016,uTime*.014)));
    float n1=fbm(p*.9+q*1.2+vec2(uTime*.02,0.)),n2=fbm(p*1.1-q*.8+vec2(-uTime*.018,uTime*.013));
    vec3 base=vec3(.028,.060,.045),gold=vec3(.89,.72,.30),green=vec3(.42,.71,.28),violet=vec3(.55,.43,.90);
    float gMask=smoothstep(0.,.7,n1)*smoothstep(.95,.10,uv.x);
    float grMask=smoothstep(0.,.7,n2)*smoothstep(.05,.90,uv.x);
    float vMask=smoothstep(0.,.55,n1*n2)*(1.-abs(uv.x-.5)*1.6);
    vec3 col=base+gold*gMask*.20+green*grMask*.16+violet*max(vMask,0.)*.13;
    col*=.30+.70*smoothstep(1.5,.35,length(p));
    gl_FragColor=vec4(sRGBenc(acesFilmic(col)),1.);
  }
`;

/* ── Yellow-sand logo (inlined from dutchie-logo-yellow-sand.svg) ── */
const LOGO_DATA_URI = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 859 207"><defs><style>.a{fill:#ffc02a}.b{fill:#f1e8d6}</style></defs><path class="b" d="M267.4 101.7c0-28.6 16.4-48.2 43.9-48.2 11.6 0 21.6 4.8 26.9 11.9V19.8c0-1.5 1.2-2.6 2.6-2.6h17.9c1.5 0 2.6 1.2 2.6 2.6v126c0 1.5-1.2 2.6-2.6 2.6h-17.9c-1.5 0-2.6-1.2-2.6-2.6v-8.1c-4.6 7.4-14.9 12.3-26.6 12.3-27.9-.1-44.3-19.7-44.3-48.3zm72.2 0c0-16.9-9.8-28-24.7-28s-24.6 11.2-24.6 28 9.8 28 24.6 28 24.7-11.2 24.7-28z"/><path class="b" d="M457.9 148.4h-17.9c-1.5 0-2.6-1.2-2.6-2.6v-11.4c-5.9 9.1-16.4 15.6-29.5 15.6-20.1 0-33.9-13.4-33.9-33.8v-58.5c0-1.5 1.2-2.6 2.6-2.6h17.9c1.5 0 2.6 1.2 2.6 2.6v53.2c0 10.1 8.1 18.5 18.2 18.4 14.1-.2 22-12.4 22-33.2v-38.3c0-1.5 1.2-2.6 2.6-2.6h17.9c1.5 0 2.6 1.2 2.6 2.6v88.2c.1 1.4-1.1 2.6-2.6 2.6z"/><path class="b" d="M516.8 149.9c-18.8 0-32.8-10.2-32.8-32.1v-43.2h-13c-1.5 0-2.6-1.2-2.6-2.6V57.7c0-1.5 1.2-2.6 2.6-2.6h13V30.3c0-1.5 1.2-2.6 2.6-2.6h17.9c1.5 0 2.6 1.2 2.6 2.6v24.8h20.8c1.5 0 2.6 1.2 2.6 2.6v14.3c0 1.5-1.2 2.6-2.6 2.6h-20.8v41c0 8.9 4.8 13.7 12 13.7 2.4 0 5.2-.2 7.8-1 1.6-.5 3.2.6 3.4 2.3l1.2 14.8c.1 1.3-.7 2.5-1.9 2.8-4 1.2-8.3 1.7-12.8 1.7z"/><path class="b" d="M534.5 101.7c0-28.6 19.3-48.2 47.2-48.2 22.7 0 39 11.3 43.2 29.6.3 1.5-.6 3-2.1 3.2l-17.6 3.5c-1.3.2-2.6-.6-3-1.9-2.4-8.5-9.8-14.1-19.7-14.1-14.4 0-23.8 11-23.8 28s9.4 28 23.8 28c10 0 16.9-5 19.7-13.6.4-1.3 1.7-2.2 3.1-1.8l17.6 3.6c1.5.3 2.4 1.8 2.1 3.3-4.5 18-20.8 28.8-43.1 28.8-27.9 0-47.3-19.7-47.3-48.2z"/><path class="b" d="M637.1 17.1h17.9c1.5 0 2.6 1.2 2.6 2.6v49.7c6.1-10.2 16.6-16 29.5-16 20.8 0 34.7 13.4 34.7 33.8v58.5c0 1.5-1.2 2.6-2.6 2.6h-17.9c-1.5 0-2.6-1.2-2.6-2.6v-52.8c0-10.2-8.1-18.7-18.2-18.7-14.5-.1-22.7 12.3-22.7 33.6v37.9c0 1.5-1.2 2.6-2.6 2.6h-17.9c-1.5 0-2.6-1.2-2.6-2.6V19.8c-.2-1.5 1-2.7 2.5-2.7z"/><path class="b" d="M730.3 28.3c0-7.9 6.1-14.3 14.4-14.3s14.8 6.3 14.8 14.3-6.2 14.5-14.8 14.5-14.4-6.3-14.4-14.5zm5.4 26.6h17.9c1.5 0 2.6 1.2 2.6 2.6v88.2c0 1.5-1.2 2.6-2.6 2.6h-17.9c-1.5 0-2.6-1.2-2.6-2.6V57.6c-.1-1.5 1.2-2.7 2.6-2.7z"/><path class="b" d="M855.3 107.8h-66.5c2 14.1 11.1 22.4 24.3 22.4 10 0 17.3-4.4 20.3-12.1.5-1.2 1.8-1.9 3.1-1.5l16.6 4.5c1.5.4 2.4 2 1.8 3.5-6 16-21.6 25.3-42.2 25.3-30.8 0-47.2-23.9-47.2-48.2s15.1-48.2 45.9-48.2 46.7 23.3 46.7 45.1c0 2.2-.1 4.7-.2 6.8-.1 1.4-1.2 2.5-2.6 2.5zm-22-17.1c-1.1-11.1-8.3-19.5-21.7-19.5-11.4 0-19.5 6.8-22.1 19.5h43.8z"/><path class="a" d="M12.4 48.1c16.9-13.6 37.9-22.5 59.6-22.9 11.6-.1 23.4 2.4 33.6 8.2 17.1 9.6 29.6 30.6 23.6 50.4-.3.8-1.4.5-1.2-.4 1.6-6.2 1.2-12.7-.6-18.8-4.7-16-18.9-27.8-34.5-31.8-7.4-2-15.3-2.5-23-1.8-1.3.1-3.3.4-4.6.6-2.1.3-4.7.9-6.8 1.4-9 2.2-17.7 5.9-25.6 10.7-5.3 3.2-10.3 6.9-14.9 11-1.8 1.6-4.5 1.4-6.1-.4-1.6-1.9-1.4-4.8.6-6.3z"/><path class="a" d="M.1 119.2c5.3-26.6 20.3-52.4 44.3-65.5 5.5-3 11.6-5.2 17.8-6.4 1.2-.2 2.6-.4 3.8-.5 2-.2 4.3-.4 6.3-.3.6 0 1.9.1 2.5.1 9.6.5 19.2 3.8 26.7 9.9 5.2 4.2 9.4 9.8 11.5 16.2.1.3-.1.7-.4.8-.3.1-.7-.1-.8-.4-2.3-6-6.5-11.1-11.6-14.9-8.3-6.2-18.8-8.7-29-8-1.3.1-3.3.3-4.7.5-.6.1-1.7.3-2.3.4-2.4.5-5 1.3-7.3 2.1-7.9 2.9-15.1 7.6-21.3 13.4C21 80.6 12 100.4 9 120.6c-.4 2.4-2.6 4-4.9 3.7-2.4-.4-4.1-2.8-3.6-5.2z"/><path class="a" d="M35.8 181.6C22.8 157.5 18 127.3 28.6 101.4c2.7-6.4 6.4-12.4 11-17.5 11.4-12.6 29.7-20 46.3-14.7 3.2 1 6.3 2.6 9 4.6.3.2.3.6.1.9-.2.3-.6.3-.9.2-13.8-9.2-32.5-5.2-44.7 5-6.8 5.6-12 13-15.3 21.2-6.1 14.9-6.3 31.8-2.6 47.4.9 3.8 2.1 7.9 3.5 11.5 2.2 6 5 11.8 8.3 17.3 1.2 2.1.6 4.8-1.4 6-2.1 1.3-4.9.5-6-1.6z"/><path class="a" d="M102.8 206.2c-25.6-10.1-48.7-30.4-56.7-57.7-2.4-8.4-3.2-17.4-1.8-26.1 2.4-15.4 12.2-30.3 27.1-35.6 3.2-1.2 6.7-1.8 10.1-1.8.4 0 .6.3.6.7 0 .4-.3.6-.6.7-3.3.1-6.5.9-9.5 2.1-18.6 7.8-26.8 29.8-23.5 49.1 3.1 18.6 15 34.7 29.7 45.8 2.5 1.9 5.3 3.8 8 5.4 6.2 3.8 12.8 6.8 19.7 9.2 2.3.8 3.5 3.3 2.7 5.5-.8 2.4-3.4 3.6-5.7 2.7z"/><path class="a" d="M169.8 181.5c-26.8 9.2-58.7 8.3-82.3-8.8-1.4-1-2.8-2.1-4.1-3.3-.5-.5-1.4-1.2-1.9-1.8-2.4-2.4-4.9-5.1-6.9-7.9-10.2-13.9-13.6-34-4.6-49.3 1.8-3 4-5.7 6.7-7.9.3-.2.7-.2.9.1.2.3.2.7-.1.9-2.4 2.2-4.4 4.9-6 7.8-7.8 14.7-3.9 33.4 6.3 46 1.9 2.5 4.3 4.9 6.7 7 .8.8 2 1.6 2.8 2.3 13.3 10.1 30.2 14.1 46.6 13.6 3.9-.1 8.1-.5 12-1.1 7.2-1.1 14.2-3.1 20.9-5.8 2.2-.9 4.7.2 5.6 2.5.9 2.3-.3 4.9-2.6 5.7z"/><path class="a" d="M205.4 119c-10.3 17.1-25.2 31.8-43.4 40.1-19.8 9.2-43.6 9.9-61.7-3.6-8.3-6.2-14.6-15.3-16.4-25.6-.6-3.4-.7-6.9-.1-10.3.1-.4.4-.6.8-.5.3.1.6.4.5.7-.4 3.3-.2 6.6.5 9.8 2.9 12.9 13.1 23.1 25 28 15 6.3 32.2 4.1 46.6-2.8 9.4-4.5 17.9-10.9 25.1-18.6 6.1-6.4 11.3-13.8 15.6-21.6 1.1-2.1 3.8-2.9 5.9-1.7 2.2 1.2 2.9 4 1.6 6.1z"/><path class="a" d="M192.9 47.9c3.7 24.6-.5 51.3-15.3 71.6-7.5 10.4-18.1 18.9-30.4 22.6-15.9 5.1-35.9 1.5-45.9-12.9-.4-.7.5-1.4 1.1-.8 3.9 5.2 9.5 8.9 15.6 10.9 12.2 4.1 25.8 1.8 36.7-4.7 22.2-13.4 32-40.9 31.7-66.1-.1-6.4-.8-12.8-2-19.1-.5-2.4 1-4.7 3.4-5.1 2.4-.5 4.7 1.2 5.1 3.6z"/><path class="a" d="M138.3 1.6c19.7 17.5 34 42.8 33.9 69.8-.1 8-1.6 16.2-4.6 23.6-4.9 11.6-13.9 21.8-25.5 26.6-6.1 2.6-12.9 3.6-19.5 2.5-.8-.2-.7-1.4.2-1.3 19.4 2.2 36.5-13.5 41.9-31.4 1.9-6 2.7-12.3 2.6-18.6-.3-15-5.8-29.6-14-42-2.1-3.2-4.6-6.5-7.1-9.4-4.1-4.8-8.6-9.1-13.4-13.1-1.9-1.5-2.2-4.3-.7-6.1 1.5-1.9 4.4-2.2 6.2-.5z"/><path class="a" d="M67 1.7c25.5.6 51.7 10.1 68.9 29.7 6.6 7.5 11.5 16.6 13.8 26.4 2.4 10.5 1.7 22-2.9 31.7-2.8 6-7 11.4-12.4 15.1-.3.2-.7.1-.9-.2-.2-.3-.1-.7.1-.9 5-3.8 8.8-9.1 11.1-14.9 3.9-9.4 4-20.1 1.3-29.9-2.7-9.7-8.4-18.5-15.6-25.5-5.5-5.4-12-9.8-18.9-13.3-8.3-4.1-17.2-6.9-26.3-8.3-6.1-1-12.2-1.4-18.3-1.2-2.4.1-4.4-1.9-4.4-4.3-.1-2.5 2-4.5 4.4-4.5z"/></svg>`
)}`;

/* ── WebGL capability detection (cached, context released immediately) ─ */
let webglSupported: boolean | null = null;
function detectWebGL(): boolean {
  if (webglSupported !== null) return webglSupported;
  try {
    const p = document.createElement('canvas');
    const gl = p.getContext('webgl2') ?? p.getContext('webgl');
    if (gl) {
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
    }
    webglSupported = !!gl;
  } catch {
    webglSupported = false;
  }
  return webglSupported;
}

/* ── Component ──────────────────────────────────────────────────────── */
type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
};

export const ConsumerAI = (): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const mouseRef = useRef({ cx: 0, cy: 0, tx: 0, ty: 0, raf: 0 });
  const cleanupRef = useRef<(() => void) | null>(null);

  /* ── WebGL aurora (Layer 3) ─────────────────────────────────────── */
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const forceNoWebGL = new URLSearchParams(window.location.search).has('nowebgl');
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isDesktop = window.matchMedia('(pointer: fine) and (min-width: 1024px)').matches;

    if (forceNoWebGL || prefersReduced || !isDesktop || !detectWebGL()) {
      return undefined;
    }

    let rafId: number | null = null;
    let disposed = false;

    const scheduleInit = (cb: () => void): number => {
      const idleWindow = window as IdleWindow;
      return typeof idleWindow.requestIdleCallback === 'function'
        ? idleWindow.requestIdleCallback(cb, { timeout: 2000 })
        : window.setTimeout(cb, 200);
    };

    scheduleInit(() => {
      if (disposed) {
        return;
      }
      try {
        const glOpts: WebGLContextAttributes = {
          antialias: false,
          alpha: false,
          depth: false,
          stencil: false,
          premultipliedAlpha: false,
          powerPreference: 'high-performance',
        };
        const gl =
          (canvas.getContext('webgl2', glOpts) as WebGLRenderingContext | null) ?? canvas.getContext('webgl', glOpts);
        if (!gl) throw new Error('no webgl');

        const compile = (type: number, src: string): WebGLShader => {
          const s = gl.createShader(type);
          if (!s) throw new Error('createShader');
          gl.shaderSource(s, src);
          gl.compileShader(s);
          if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
            throw new Error(gl.getShaderInfoLog(s) ?? 'compile');
          }
          return s;
        };
        const prog = gl.createProgram();
        if (!prog) throw new Error('createProgram');
        gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
        gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
        gl.linkProgram(prog);
        if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
          throw new Error(gl.getProgramInfoLog(prog) ?? 'link');
        }
        gl.useProgram(prog);

        const buf = gl.createBuffer();
        if (!buf) throw new Error('createBuffer');
        gl.bindBuffer(gl.ARRAY_BUFFER, buf);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
        const aPos = gl.getAttribLocation(prog, 'aPos');
        gl.enableVertexAttribArray(aPos);
        gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

        const uTime = gl.getUniformLocation(prog, 'uTime');
        const uAspect = gl.getUniformLocation(prog, 'uAspect');
        const uOctaves = gl.getUniformLocation(prog, 'uOctaves');
        gl.disable(gl.DEPTH_TEST);
        gl.uniform1i(uOctaves, 3);

        let timeVal = 0;
        let lastT = 0;

        const resize = (): void => {
          const w = container.offsetWidth;
          const h = container.offsetHeight;
          const pr = Math.min(window.devicePixelRatio, 1.75);
          canvas.width = Math.max(1, Math.round(w * pr));
          canvas.height = Math.max(1, Math.round(h * pr));
          gl.viewport(0, 0, canvas.width, canvas.height);
          gl.uniform1f(uAspect, w / h);
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(container);
        window.addEventListener('resize', resize, { passive: true });

        canvas.style.opacity = '0';
        canvas.style.transition = 'opacity 0.8s ease';
        canvas.style.display = 'block';

        const loop = (): void => {
          if (disposed) {
            return;
          }
          rafId = requestAnimationFrame(loop);
          const now = performance.now();
          timeVal += lastT > 0 ? Math.min((now - lastT) / 1000, 0.05) : 0;
          lastT = now;
          gl.uniform1f(uTime, timeVal);
          gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
          if (canvas.style.opacity === '0') {
            canvas.style.opacity = '1';
          }
        };

        // Only render when the tab is visible AND the panel is on-screen.
        let tabVisible = !document.hidden;
        let intersecting = true;
        const start = (): void => {
          if (!disposed && tabVisible && intersecting && rafId === null) {
            lastT = 0; // reset so first delta doesn't jump after idle
            loop();
          }
        };
        const stop = (): void => {
          if (rafId !== null) {
            cancelAnimationFrame(rafId);
            rafId = null;
          }
        };

        const onVis = (): void => {
          tabVisible = !document.hidden;
          if (tabVisible) {
            start();
          } else {
            stop();
          }
        };
        document.addEventListener('visibilitychange', onVis);
        const io = new IntersectionObserver(([e]) => {
          intersecting = e.isIntersecting;
          if (intersecting) {
            start();
          } else {
            stop();
          }
        });
        io.observe(container);

        canvas.addEventListener('webglcontextlost', (e) => {
          e.preventDefault();
          stop();
        });
        canvas.addEventListener('webglcontextrestored', () => {
          resize();
          start();
        });

        start();

        cleanupRef.current = (): void => {
          disposed = true;
          stop();
          window.removeEventListener('resize', resize);
          document.removeEventListener('visibilitychange', onVis);
          ro.disconnect();
          io.disconnect();
          gl.deleteProgram(prog);
          gl.deleteBuffer(buf);
          const loseCtx = gl.getExtension('WEBGL_lose_context');
          if (loseCtx) loseCtx.loseContext();
        };
      } catch {
        /* WebGL unavailable — video + CSS gradient fallback stays visible */
      }
    });

    return () => {
      disposed = true;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, []);

  /* ── 3D parallax mouse tracking ─────────────────────────────────── */
  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const cards = cardsRef.current;
    if (!cards) {
      return;
    }
    const m = mouseRef.current;
    const r = e.currentTarget.getBoundingClientRect();
    m.tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
    m.ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
    const loop = () => {
      m.cx += (m.tx - m.cx) * 0.07;
      m.cy += (m.ty - m.cy) * 0.07;
      cards.style.setProperty('--ry', `${(m.cx * 6).toFixed(2)}deg`);
      cards.style.setProperty('--rx', `${(-m.cy * 6).toFixed(2)}deg`);
      if (Math.abs(m.tx - m.cx) > 0.001 || Math.abs(m.ty - m.cy) > 0.001) {
        m.raf = requestAnimationFrame(loop);
      }
    };
    cancelAnimationFrame(m.raf);
    m.raf = requestAnimationFrame(loop);
  }, []);

  const onMouseLeave = useCallback(() => {
    const cards = cardsRef.current;
    if (!cards) {
      return;
    }
    const m = mouseRef.current;
    m.tx = 0;
    m.ty = 0;
    const loop = () => {
      m.cx += (m.tx - m.cx) * 0.07;
      m.cy += (m.ty - m.cy) * 0.07;
      cards.style.setProperty('--ry', `${(m.cx * 6).toFixed(2)}deg`);
      cards.style.setProperty('--rx', `${(-m.cy * 6).toFixed(2)}deg`);
      if (Math.abs(m.cx) > 0.001 || Math.abs(m.cy) > 0.001) {
        m.raf = requestAnimationFrame(loop);
      }
    };
    cancelAnimationFrame(m.raf);
    m.raf = requestAnimationFrame(loop);
  }, []);

  return (
    <Wrapper ref={containerRef} onMouseLeave={onMouseLeave} onMouseMove={onMouseMove}>
      {/* Layer 1 — CSS gradient fallback (instant, always visible) */}
      {/* Layer 2 — video loop (hardware decode, fine on all devices) */}
      <AuroraVideo aria-hidden='true' autoPlay loop muted playsInline>
        <source src='/assets/aurora-bg.webm' type='video/webm' />
      </AuroraVideo>
      {/* Layer 3 — live WebGL aurora, fades in on capable desktop after idle */}
      <AuroraCanvas ref={canvasRef} />
      <HubOrb />
      <Content>
        <LogoImg alt='Dutchie' src={LOGO_DATA_URI} />
        <TextContainer>
          <Kicker>
            <KickerDot />
            New &middot; Consumer AI Suite
          </Kicker>
          <Headline data-testid='login-marketing-highlight'>
            Catch every call,
            <br />
            chat, cart &amp; review.
            <br />
            <HeadlineEm>One AI.</HeadlineEm>
          </Headline>
          <Cta
            aria-label='Explore Consumer AI — opens in a new tab'
            href='https://dutchie.com/business/consumer-ai-demo?utm_source=backoffice&utm_medium=banner&utm_campaign=consumerailaunchjun26'
            rel='noreferrer'
            target='_blank'
          >
            Explore Consumer AI
          </Cta>
          <CardsGrid ref={cardsRef}>
            {/* Card 1 — Voice AI (gold) */}
            <Card $delay1='0.55s' $delay2='1.9s' $dur='7.2s' $z='18px'>
              <CardHeader>
                <CardTag $color='#E3B84C'>
                  <Pulse $color='#E3B84C' />
                  Voice AI
                </CardTag>
                <CardMeta>0:42</CardMeta>
              </CardHeader>
              <CardName>Inbound call &middot; +1 (303) 555&hellip;</CardName>
              <CardLine>&ldquo;Do you have Blue Dream in stock?&rdquo;</CardLine>
              <Wave>
                {([
                  { id: 'bar0', delay: '0s' },
                  { id: 'bar1', delay: '0.1s' },
                  { id: 'bar2', delay: '0.2s' },
                  { id: 'bar3', delay: '0.3s' },
                  { id: 'bar4', delay: '0.15s' },
                  { id: 'bar5', delay: '0.25s' },
                  { id: 'bar6', delay: '0.05s' },
                  { id: 'bar7', delay: '0.35s' },
                  { id: 'bar8', delay: '0.2s' },
                  { id: 'bar9', delay: '0.1s' },
                ] as const).map(({ id, delay }) => (
                  <WaveBar $delay={delay} key={id} />
                ))}
              </Wave>
            </Card>

            {/* Card 2 — Agentic Commerce (green) */}
            <Card
              $accent='rgba(106,180,72,0.30)'
              $delay1='0.7s'
              $delay2='2.6s'
              $dur='8.4s'
              $glow='rgba(106,180,72,0.08)'
              $z='-26px'
            >
              <CardHeader>
                <CardTag $color='#6AB448'>Agentic Commerce</CardTag>
                <CardMeta>Cart &middot; 3</CardMeta>
              </CardHeader>
              <CardItem>
                <CardItemName>Wyld &middot; Huckleberry</CardItemName>
                <CardPrice $color='#6AB448'>$24</CardPrice>
              </CardItem>
              <CardItem>
                <CardItemName>Camino &middot; Midnight</CardItemName>
                <CardPrice $color='#6AB448'>$22</CardPrice>
              </CardItem>
              <CardRow>
                <Checkmark>&#10003;</Checkmark>Reorder ready in one tap
              </CardRow>
            </Card>

            {/* Card 3 — Register Co-Pilot (gold) */}
            <Card $delay1='0.85s' $delay2='2.2s' $dur='6.6s' $z='34px'>
              <CardHeader>
                <CardTag $color='#E3B84C'>
                  <Pulse $color='#E3B84C' />
                  Register Co-Pilot
                </CardTag>
                <CardMeta>POS</CardMeta>
              </CardHeader>
              <CardLine>Suggested upsell for this basket</CardLine>
              <CardItem>
                <CardItemName>Pre-roll 5pk</CardItemName>
                <CardPrice $color='#E3B84C'>+$32</CardPrice>
              </CardItem>
              <CardRow>
                <Checkmark>&#10003;</Checkmark>Accepted by budtender
              </CardRow>
            </Card>

            {/* Card 4 — Consumer Pulse (violet) */}
            <Card
              $accent='rgba(139,111,230,0.32)'
              $delay1='1s'
              $delay2='3.1s'
              $dur='9s'
              $glow='rgba(139,111,230,0.08)'
              $z='-14px'
            >
              <CardHeader>
                <CardTag $color='#8B6FE6'>
                  <Pulse $color='#8B6FE6' />
                  Consumer Pulse
                </CardTag>
                <CardMeta>Today</CardMeta>
              </CardHeader>
              <BigStat>
                +18<BigStatUnit>&nbsp;CSAT</BigStatUnit>
              </BigStat>
              <CardLineSpaced>
                Sentiment up across 12 locations&nbsp;&mdash; surfaced from reviews &amp; surveys.
              </CardLineSpaced>
            </Card>
          </CardsGrid>
        </TextContainer>
      </Content>
    </Wrapper>
  );
};

/* ── Styled Components ──────────────────────────────────────────────── */

/* Outer wrapper — extends BaseSlideWrapper (45% width, responsive breakpoints)
   with aurora CSS gradient fallback as Layer 1 */
const Wrapper = styled(BaseSlideWrapper)`
  position: relative;
  overflow: hidden;
  height: 100vh;
  background: radial-gradient(900px 600px at 70% 40%, rgba(106, 180, 72, 0.18), transparent 60%),
    radial-gradient(700px 500px at 20% 70%, rgba(227, 184, 76, 0.1), transparent 60%),
    linear-gradient(160deg, #0b1610 0%, #070e0a 100%);

  @media ${mediaQueries.largeDesktop} {
    height: auto;
    min-height: 420px;
  }
`;

/* WebGL canvas — hidden by default; JS sets display:block + fades in */
const AuroraCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: none;
`;

/* Video fallback — hardware-decoded, negligible cost vs live shader.
   Hidden for prefers-reduced-motion (CSS gradient shows instead). */
const AuroraVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

/* Pulsing gold-green radial orb overlay */
const HubOrb = styled.div`
  position: absolute;
  top: 42%;
  left: 72%;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    rgba(227, 184, 76, 0.1) 0%,
    rgba(227, 184, 76, 0.04) 35%,
    rgba(106, 180, 72, 0.03) 60%,
    transparent 75%
  );
  filter: blur(8px);
  pointer-events: none;
  animation: hubpulse 14s ease-in-out infinite;

  @keyframes hubpulse {
    0%,
    100% {
      transform: translate(-50%, -50%) scale(1);
    }
    50% {
      transform: translate(-50%, -50%) scale(1.08);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Content = styled(BaseSlideContent)`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 100px 52px 40px;
  color: #f0e7d4;
  width: 100%;
`;

const LogoImg = styled.img`
  position: absolute;
  top: 48px;
  left: 72px;
  width: 140px;
  height: auto;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 440px;
`;

const Kicker = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #e3b84c;
  margin-bottom: 24px;
`;

const KickerDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e3b84c;
  box-shadow: 0 0 12px #e3b84c;
  flex-shrink: 0;
`;

const Headline = styled.h2`
  font-weight: 200;
  font-size: clamp(32px, 3.2vw, 52px);
  line-height: 1.05;
  letter-spacing: -0.025em;
  color: #faf8f4;
  margin: 0 0 20px;

  @media ${mediaQueries.belowMedium} {
    font-size: 34px;
  }
`;

const HeadlineEm = styled.em`
  font-style: italic;
  color: #e3b84c;
  font-weight: 200;
`;

const Cta = styled(BaseSlideCta)`
  background: #e3b84c;
  border: 1px solid #e3b84c;
  color: #1b1305;
  font-weight: 600;
  letter-spacing: 0.01em;

  &:hover {
    background: #efc96a;
    border-color: #efc96a;
    color: #1b1305;
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(227, 184, 76, 0.28);
  }
`;

/* ── Cards ──────────────────────────────────────────────────────────── */

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  width: 100%;
  margin-top: 28px;
  perspective: 1100px;
  transform-style: preserve-3d;
  --rx: 0deg;
  --ry: 0deg;
  transform: translate3d(0, 0, 0) rotateX(var(--rx)) rotateY(var(--ry));
  transition: transform 0.12s ease-out;

  @media (prefers-reduced-motion: reduce) {
    transform: none !important;
    transition: none;
  }
`;

const Card = styled.div<{
  $accent?: string;
  $delay1: string;
  $delay2: string;
  $dur: string;
  $glow?: string;
  $z: string;
}>`
  position: relative;
  border-radius: 9px;
  background: linear-gradient(180deg, rgba(20, 30, 24, 0.92), rgba(12, 20, 16, 0.92));
  border: 1px solid ${(p) => p.$accent ?? 'rgba(227,184,76,0.30)'};
  padding: 13px 14px;
  box-shadow: 0 22px 50px rgba(0, 0, 0, 0.5), 0 0 60px ${(p) => p.$glow ?? 'rgba(227,184,76,0.08)'};
  overflow: hidden;
  transform-style: preserve-3d;
  will-change: transform;
  opacity: 0;
  --z: ${(p) => p.$z};
  --dur: ${(p) => p.$dur};
  animation: cairise 1s cubic-bezier(0.22, 0.61, 0.36, 1) ${(p) => p.$delay1} forwards,
    caifloat var(--dur) ease-in-out ${(p) => p.$delay2} infinite;

  @keyframes cairise {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes caifloat {
    0% {
      transform: translate3d(0, 0, var(--z)) rotateX(0deg) rotateY(0deg);
    }
    25% {
      transform: translate3d(0, -7px, calc(var(--z) + 10px)) rotateX(1.4deg) rotateY(-1.2deg);
    }
    50% {
      transform: translate3d(0, -3px, calc(var(--z) - 6px)) rotateX(-1deg) rotateY(1.4deg);
    }
    75% {
      transform: translate3d(0, 5px, calc(var(--z) + 6px)) rotateX(1deg) rotateY(0.8deg);
    }
    100% {
      transform: translate3d(0, 0, var(--z)) rotateX(0deg) rotateY(0deg);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const CardTag = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 8px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${(p) => p.$color};
  font-weight: 600;
`;

const Pulse = styled.span<{ $color: string }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  box-shadow: 0 0 8px ${(p) => p.$color};
  flex-shrink: 0;
  animation: caipulse 1.4s ease-in-out infinite;

  @keyframes caipulse {
    0%,
    100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.4;
      transform: scale(0.8);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardMeta = styled.div`
  font-size: 9px;
  color: rgba(250, 248, 244, 0.4);
`;

const CardName = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #faf8f4;
  margin-bottom: 3px;
`;

const CardLine = styled.div`
  font-size: 10px;
  color: rgba(250, 248, 244, 0.65);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const CardLineSpaced = styled(CardLine)`
  margin-top: 8px;
`;

const CardItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 7px;
  margin-bottom: 4px;
  border-radius: 4px;
  background: rgba(250, 248, 244, 0.04);
  border: 1px solid rgba(250, 248, 244, 0.06);
  font-size: 10px;
`;

const CardItemName = styled.span`
  font-weight: 500;
  color: #faf8f4;
`;

const CardPrice = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-weight: 500;
`;

const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
  color: rgba(250, 248, 244, 0.65);
  margin-top: 2px;
`;

const Checkmark = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: rgba(106, 180, 72, 0.2);
  color: #6ab448;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  flex-shrink: 0;
`;

const Wave = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5px;
  height: 18px;
`;

const WaveBar = styled.i<{ $delay: string }>`
  display: block;
  width: 2px;
  height: 16px;
  border-radius: 1px;
  background: #e3b84c;
  opacity: 0.85;
  transform-origin: center;
  animation: caiwave 1.1s ease-in-out ${(p) => p.$delay} infinite;

  @keyframes caiwave {
    0%,
    100% {
      transform: scaleY(0.25);
    }
    50% {
      transform: scaleY(1);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transform: scaleY(0.5);
  }
`;

const BigStat = styled.div`
  font-weight: 200;
  font-size: 28px;
  letter-spacing: -0.02em;
  color: #faf8f4;
  line-height: 1;
  margin-bottom: 6px;
`;

const BigStatUnit = styled.span`
  color: #6ab448;
  font-size: 12px;
  font-weight: 500;
`;
